import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  Mail,
  Smartphone,
  Monitor,
  Moon,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';

interface NotificationPreferences {
  id: string;
  emailEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  preferences: Record<string, Record<string, boolean>>;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone: string;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  id: '',
  emailEnabled: true,
  pushEnabled: true,
  inAppEnabled: true,
  preferences: {},
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  timezone: 'UTC',
};

const EVENT_TYPES = [
  { key: 'ticket_replied', label: 'Agent replied to my ticket' },
  { key: 'ticket_status_changed', label: 'Ticket status changed' },
  { key: 'ticket_resolved', label: 'Ticket resolved' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i.toString().padStart(2, '0');
  return { value: `${h}:00`, label: `${h}:00` };
});

export function NotificationPreferencesPage() {
  const { isAuthenticated, customer } = useAuthStore();
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const loadPreferences = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get('/notifications/preferences');
        setPrefs({ ...DEFAULT_PREFERENCES, ...response.data });
      } catch {
        // Use defaults if service unavailable
        setPrefs(DEFAULT_PREFERENCES);
      } finally {
        setIsLoading(false);
      }
    };
    loadPreferences();
  }, [isAuthenticated]);

  const updateChannel = (field: 'emailEnabled' | 'pushEnabled' | 'inAppEnabled', value: boolean) => {
    setPrefs((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccess(null);
  };

  const updateEventPref = (eventKey: string, channel: string, value: boolean) => {
    setPrefs((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [eventKey]: {
          ...prev.preferences[eventKey],
          [channel]: value,
        },
      },
    }));
    setHasChanges(true);
    setSuccess(null);
  };

  const updateQuietHours = (field: string, value: string | boolean) => {
    setPrefs((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccess(null);
  };

  const getEventPref = (eventKey: string, channel: string): boolean => {
    return prefs.preferences[eventKey]?.[channel] ?? true;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.put('/notifications/preferences', {
        emailEnabled: prefs.emailEnabled,
        pushEnabled: prefs.pushEnabled,
        inAppEnabled: prefs.inAppEnabled,
        preferences: prefs.preferences,
        quietHoursEnabled: prefs.quietHoursEnabled,
        quietHoursStart: prefs.quietHoursStart || '22:00',
        quietHoursEnd: prefs.quietHoursEnd || '08:00',
        timezone: prefs.timezone || customer?.timezone || 'UTC',
      });
      setPrefs({ ...DEFAULT_PREFERENCES, ...response.data });
      setSuccess('Notification preferences saved successfully.');
      setHasChanges(false);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to save preferences. Please try again.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="py-16">
        <div className="container max-w-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Sign in required</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Please sign in to manage your notification preferences.</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <div className="container max-w-2xl flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="py-16">
      <div className="container max-w-2xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-primary-600">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/profile" className="hover:text-primary-600">My Profile</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-gray-900 dark:text-white font-medium">Notification Preferences</span>
        </nav>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Section 1: Channel Toggles */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Notification Preferences</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Choose how and when you'd like to be notified.</p>

          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
            Notification Channels
          </h2>

          <div className="space-y-4">
            {/* Email Toggle */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Receive notifications via email</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs.emailEnabled}
                onClick={() => updateChannel('emailEnabled', !prefs.emailEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  prefs.emailEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    prefs.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Push Toggle */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Push Notifications</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Receive push notifications on your device</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs.pushEnabled}
                onClick={() => updateChannel('pushEnabled', !prefs.pushEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  prefs.pushEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    prefs.pushEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* In-App Toggle */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Monitor className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">In-App Notifications</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">See notifications inside the portal</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs.inAppEnabled}
                onClick={() => updateChannel('inAppEnabled', !prefs.inAppEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  prefs.inAppEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    prefs.inAppEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Section 2: Event Preferences */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
            Event Preferences
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Choose which events you want to be notified about per channel.
          </p>

          {/* Table Header */}
          <div className="grid grid-cols-[1fr_60px_60px] gap-4 mb-3 px-1">
            <div />
            <div className="text-center">
              <Mail className="h-4 w-4 text-gray-400 mx-auto dark:text-gray-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Email</span>
            </div>
            <div className="text-center">
              <Smartphone className="h-4 w-4 text-gray-400 mx-auto dark:text-gray-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Push</span>
            </div>
          </div>

          {/* Event Rows */}
          <div className="space-y-1">
            {EVENT_TYPES.map((event) => (
              <div
                key={event.key}
                className="grid grid-cols-[1fr_60px_60px] gap-4 items-center py-3 border-b border-gray-100 dark:border-gray-700 px-1"
              >
                <p className="text-sm text-gray-900 dark:text-white">{event.label}</p>
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={prefs.emailEnabled && getEventPref(event.key, 'email')}
                    disabled={!prefs.emailEnabled}
                    onChange={(e) => updateEventPref(event.key, 'email', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40 dark:border-gray-700"
                  />
                </div>
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={prefs.pushEnabled && getEventPref(event.key, 'push')}
                    disabled={!prefs.pushEnabled}
                    onChange={(e) => updateEventPref(event.key, 'push', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40 dark:border-gray-700"
                  />
                </div>
              </div>
            ))}
          </div>

          {(!prefs.emailEnabled && !prefs.pushEnabled) && (
            <p className="mt-4 text-xs text-amber-600">
              Both email and push are disabled above. Enable at least one channel to receive event notifications.
            </p>
          )}
        </div>

        {/* Section 3: Quiet Hours */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Moon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <div>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Quiet Hours
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Pause notifications during specified hours.
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={prefs.quietHoursEnabled}
              onClick={() => updateQuietHours('quietHoursEnabled', !prefs.quietHoursEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                prefs.quietHoursEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  prefs.quietHoursEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {prefs.quietHoursEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-8">
              <div>
                <label htmlFor="quietStart" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  From
                </label>
                <select
                  id="quietStart"
                  value={prefs.quietHoursStart || '22:00'}
                  onChange={(e) => updateQuietHours('quietHoursStart', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-700"
                >
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quietEnd" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  To
                </label>
                <select
                  id="quietEnd"
                  value={prefs.quietHoursEnd || '08:00'}
                  onChange={(e) => updateQuietHours('quietHoursEnd', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 dark:border-gray-700"
                >
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/profile"
            className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Back to Profile
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
